(ns practitest.frontend.global-fields-settings.entity-linker
  (:require
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components.datepicker :as dp]
    [practitest.frontend.components.datepicker2 :as dp2]
    [practitest.frontend.components.selector :as selector2]
    [practitest.frontend.common.i18n :refer [get-translation]]))

;; Field formats that never support a Default Value, mirroring
;; CustomField#supports_default_value? on the backend.
(def default-value-unsupported-formats #{"url" "linkedlist" "checkbox"})

(def ^:private mandatory-unsupported-formats #{"checkbox"})

(def ^:private rows
  [{:type :entity
    :id :test
    :name (get-translation "test.singular" "Test")
    :input-type "checkbox"}

   {:type :entity
    :id :requirement
    :name (get-translation "requirement.singular" "Requirement")
    :input-type "checkbox"}

   {:type :entity
    :id :issue
    :name (get-translation "issue.singular" "Issue")
    :input-type "checkbox"}

   {:type :entity
    :id :milestone
    :name (get-translation "milestone.singular" "Milestone")
    :input-type "checkbox"}

   {:type :subtitle
    :label (get-translation "testset.grid.title" "TestSets & Runs")}

   {:type :none-radio
    :id :none
    :name (get-translation "generic.label.none" "None")
    :input-type "radio"}

   {:type :entity
    :id :testSets
    :name (get-translation "testset.singular" "TestSet")
    :input-type "radio"}

   {:type :entity
    :id :instances
    :name (get-translation "instance.singular" "Instance")
    :input-type "radio"}

   {:type :entity
    :id :run
    :name (get-translation "testset.button.run" "Run")
    :input-type "radio"}])

(defn- boolean-input [{:keys [type checked disabled on-change name]}]
  [:input {:type type
           :name name
           :checked checked
           :disabled disabled
           :class (if (= type "checkbox") "m-checkbox" "custom-radio-input")
           :on-change #(on-change (.. % -target -checked))}])

(defn- update-linked [table-data current-id input-type is-checked?]
  (cond
    (= input-type "checkbox")
    (assoc-in table-data [current-id :linked] is-checked?)

    (= input-type "radio")
    (let [radio-rows (filter #(and (= "radio" (:input-type %))
                                   (= :entity (:type %))) rows)]
      (reduce
        (fn [acc {:keys [id]}]
          (assoc-in acc [id :linked] (= id current-id)))
        table-data
        radio-rows))))

(defn- default-value-widget [{:keys [id table-data change-value linked? field-type possible-values users]}]
  (let [current        (get-in table-data [id :default])
        set-with-check (fn [v]
                         (change-value
                           (-> table-data
                               (assoc-in [id :default] v)
                               (assoc-in [id :has-default?] (boolean v)))))]
    (cond
      (#{"date" "datetime"} field-type)
      (if (= field-type "date")
        [dp2/date-picker-input
         {:value     current
          :class     "m-input"
          :disabled? (not linked?)
          :on-change (fn [tick-date]
                       (set-with-check (when tick-date (str tick-date))))}]
        [dp/instant-picker
         {:value     current
          :class     "m-input"
          :disabled? (not linked?)
          :on-change (fn [tick-instant]
                       (set-with-check (when tick-instant (str tick-instant))))}])

      possible-values
      [selector2/new-select
       {:items possible-values
        :label-fn identity
        :key-fn identity
        :clearable? true
        :disabled? (not linked?)
        :class "default-value-selector"
        :value (when-not (empty? current) current)
        :on-change (fn [v] (set-with-check v))}]

      users
      (let [selected-user (some #(when (= (str (:id %)) current) %) users)]
        [selector2/new-select
         {:items users
          :label-fn :name
          :key-fn :id
          :clearable? true
          :disabled? (not linked?)
          :class "default-value-selector"
          :value selected-user
          :on-change (fn [u] (set-with-check (some-> u :id str)))}])

      :else
      [:input.search-input
       {:type      (if (= field-type "number") "number" "text")
        :value     (or current "")
        :read-only (not linked?)
        :on-change (fn [e]
                     (let [v (.. e -target -value)]
                       (change-value
                         (-> table-data
                             (assoc-in [id :default] v)
                             (assoc-in [id :has-default?] (boolean (seq v)))))))}])))

(defn- table-entity-row [{:keys [id name input-type]} table-data change-value field-type show-default-column? default-value-opts show-mandatory? disabled-entities]
  (let [link-entity?         (boolean (get-in table-data [id :linked]))
        has-default?         (boolean (get-in table-data [id :has-default?]))
        mandatory-supported? (not (mandatory-unsupported-formats field-type))
        entity-disabled?     (boolean (and disabled-entities (disabled-entities id)))]
    [:tr
     [:td.entity-type
      (if entity-disabled?
        [tooltip/mux-tooltip {}
         (get-translation "custom-fields.entity-linker.managed-at-account-level"
                          "Entity linkage is managed in account settings")
         (fn [props]
           [:div (merge props {:style {:display "flex" :align-items "center"}})
            [boolean-input {:type input-type :checked link-entity? :disabled true :on-change identity :name "entity-type"}]
            [:span name]])]
        [:<>
         [boolean-input
          {:type input-type
           :checked link-entity?
           :disabled false
           :on-change (fn [is-checked?] (change-value (update-linked table-data id input-type is-checked?)))
           :name "entity-type"}]
         [:span name]])]
     (when show-mandatory?
       [:td.mandatory-check
        (if mandatory-supported?
          [boolean-input
           {:type "checkbox"
            :checked (get-in table-data [id :mandatory])
            :disabled (not link-entity?)
            :on-change (fn [is-checked?] (change-value (assoc-in table-data [id :mandatory] is-checked?)))}]
          [tooltip/mux-tooltip {}
           (get-translation "custom-fields.entity-linker.not-supported" "Not available for this field type")
           (fn [props]
             [:div props
              [boolean-input {:type "checkbox"
                              :checked false
                              :disabled true
                              :on-change identity}]])])])
     (when show-default-column?
       [:td.default-value-cell
        (if default-value-opts
          [:div.default-value-inner
           [boolean-input
            {:type "checkbox"
             :checked has-default?
             :disabled (not link-entity?)
             :on-change (fn [is-checked?]
                          (change-value (cond-> (assoc-in table-data [id :has-default?] is-checked?)
                                          (not is-checked?) (assoc-in [id :default] nil))))}]
           [default-value-widget (merge default-value-opts
                                        {:id          id
                                         :table-data  table-data
                                         :change-value change-value
                                         :linked?     link-entity?})]]
          [tooltip/mux-tooltip {}
           (get-translation "custom-fields.entity-linker.not-supported" "Not available for this field type")
           (fn [props]
             [:div.default-value-inner props
              [boolean-input {:type "checkbox" :checked false :disabled true :on-change identity}]
              [:input.search-input {:type "text" :value "" :disabled true}]])])])]))

(defn- subtitle-row [{:keys [label]} col-span]
  [:tr.subtitle
   [:td {:col-span col-span}
    label]])

(defn- none-radio-row [{:keys [id name input-type]} table-data change-value col-span]
  (let [any-entity-linked? (some #(get-in table-data [(:id %) :linked])
                                   (filter #(and (= "radio" (:input-type %))
                                                 (= :entity (:type %))) rows))]
  [:tr
   [:td.entity-type {:col-span col-span}
     [boolean-input
      {:type input-type
       :name "entity-type"
       :checked (not any-entity-linked?)
       :on-change (fn [is-checked?] (change-value (update-linked table-data id input-type is-checked?)))}]
     [:span name]]]))

(defn entity-linker-table [table-id form-props & [{:keys [available? field-format show-default-value? show-mandatory? possible-values users allowed-entities disabled-entities testset-as-checkboxes?]}]]
  (let [{:keys [get-field-state set-field-state]} form-props
        table-data (or (:value @(get-field-state table-id)) {})
        change-value (fn [new-value]
                       (set-field-state table-id {:value new-value
                                                  :dirty? true}))
        ;; Fall back to form-state key for Global Fields (which passes no opts)
        available? (if (some? available?)
                     available?
                     (:value @(get-field-state :global-field-select-projects)))
        field-type (or field-format (:value @(get-field-state :global-field-format)))
        show-default-column? (boolean show-default-value?)
        default-value-opts (when (and show-default-column?
                                      (not (default-value-unsupported-formats field-type)))
                             {:field-type      field-type
                              :possible-values possible-values
                              :users           (when (= field-type "user") users)})
        ;; nil means "not specified" -- default to showing mandatory (backward-compat)
        show-mandatory? (if (some? show-mandatory?) show-mandatory? true)
        col-span (+ 1
                    (if show-mandatory? 1 0)
                    (if show-default-column? 1 0))
        effective-rows (if testset-as-checkboxes?
                         (keep (fn [row]
                                 (case (:type row)
                                   :none-radio nil
                                   :entity     (assoc row :input-type
                                                 (if (= "radio" (:input-type row))
                                                   "checkbox"
                                                   (:input-type row)))
                                   row))
                               rows)
                         rows)
        testset-entity-ids #{:testSets :instances :run}
        has-radio-entity? (some #(and (= :entity (:type %))
                                      (= "radio" (:input-type %))
                                      (or (nil? allowed-entities)
                                          (allowed-entities (:id %))))
                                effective-rows)
        has-testset-entity? (some #(and (= :entity (:type %))
                                        (testset-entity-ids (:id %))
                                        (or (nil? allowed-entities)
                                            (allowed-entities (:id %))))
                                  effective-rows)
        filtered-rows (if (seq allowed-entities)
                        (filter (fn [row]
                                  (case (:type row)
                                    :entity     (allowed-entities (:id row))
                                    :subtitle   (or has-radio-entity? has-testset-entity?)
                                    :none-radio has-radio-entity?))
                                effective-rows)
                        effective-rows)]
    [:div.global-entity-linker-wrapper
     [:table {:class (str "new-grid global-entity-linker"
                          (when show-default-column? " has-default-column")
                          (when (not available?) " disabled"))}
      [:thead
       [:tr
        [:th (get-translation "generic.label.entity-type" "Entity type")]
        (when show-mandatory?
          [:th (get-translation "generic.label.mandatory" "Mandatory")])
        (when show-default-column?
          [:th (get-translation "generic.label.default-value" "Default Value")])]]
      [:tbody
       (for [row filtered-rows]
          (case (:type row)
            :entity
            ^{:key (:id row)}
            [table-entity-row row table-data change-value field-type show-default-column? default-value-opts show-mandatory? disabled-entities]

            :subtitle
            ^{:key (:label row)}
            [subtitle-row row col-span]

            :none-radio
            ^{:key (:id row)}
            [none-radio-row row table-data change-value col-span]))]]]))
